/**
 * The loud "new order / new offer" ring, played inside the app.
 *
 * A push in the tray rings through the Android channel (res/raw/order_ring);
 * this covers everything else: the app open in the foreground (Android shows
 * no tray notification then), the admin panel in a browser, and an offer card
 * that keeps ringing until the partner answers it.
 *
 * Each alert has a key (offer or order id) so the push, the poll and the offer
 * card can all ask for the same ring without it playing twice.
 */

const SRC = `${import.meta.env.BASE_URL}sounds/order-ring.mp3`;
const PING_SRC = `${import.meta.env.BASE_URL}sounds/notify-ping.mp3`;

let audio = null;
let playingKey = null;
let stopTimer = null;
const done = new Set(); // keys that have already rung this session

function vibrate() {
  try {
    navigator.vibrate?.([900, 400, 900, 400, 900]);
  } catch {
    /* not supported */
  }
}

/**
 * Ring for `key`. `loop` keeps ringing (up to `maxMs`) until stopRing(key).
 * Asking again for a key that is ringing can upgrade it to a loop; a key that
 * already finished never rings again.
 */
export function ring(key, { loop = false, maxMs = 30_000 } = {}) {
  if (key && key === playingKey && audio) {
    if (loop && !audio.loop) {
      audio.loop = true;
      clearTimeout(stopTimer);
      stopTimer = setTimeout(() => stopRing(key), maxMs);
    }
    return;
  }
  if (key && done.has(key)) return;
  if (key) done.add(key);

  stopRing();
  try {
    audio = new Audio(SRC);
    audio.loop = loop;
    playingKey = key ?? null;
    audio.addEventListener('ended', () => {
      if (playingKey === key) playingKey = null;
    });
    // Browsers refuse sound before the first tap on the page; nothing to do then.
    audio.play().catch(() => {});
    vibrate();
    if (loop) stopTimer = setTimeout(() => stopRing(key), maxMs);
  } catch {
    /* no audio support */
  }
}

/**
 * The ordinary notification sound: a short chime for everything that isn't a
 * new order or a delivery offer — a payout request, a review, low stock. Never
 * interrupts a ring that is already playing.
 */
export function ping(key) {
  if (key && done.has(key)) return;
  if (key) done.add(key);
  if (audio) return; // a ring is playing; don't talk over it
  try {
    const chime = new Audio(PING_SRC);
    chime.play().catch(() => {});
  } catch {
    /* no audio support */
  }
}

/** Stop the ring (only if it's `key`'s, when a key is given). */
export function stopRing(key) {
  if (key && key !== playingKey) return;
  clearTimeout(stopTimer);
  stopTimer = null;
  if (audio) {
    audio.pause();
    audio = null;
  }
  playingKey = null;
}
